import React, { memo, useState } from "react";
import { Box, Button, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import IpdDateField from "../../../../utils/IpdDateField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import { toast } from "react-toastify";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { DOC_TYPES } from "../../constants/registrationConstants";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { useSectionConfig } from "../../context/SectionConfigContext";
import { readFileAsBase64 } from "../../utils/readFileAsBase64";
import { getDocumentNumberLengthRule } from "../../utils/validation";

const S = CONFIGURED_SECTION_IDS;
const DEFAULT_DOC = {
  type: "AADHAAR", number: "", issuedBy: "", expiry: "", fileName: "", fileBase64: "",
};

function Documents({ patient, setPatient, updateListItem, addListItem, removeListItem, errors = {} }) {
  const {
    canAddListEntry, canRemoveListEntry, isFieldActive, isFieldMandatory, config, getListFieldGridSize,
  } = useSectionConfig();
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const md = config ? getListFieldGridSize(S.documents) : 2;
  const showRemove = config ? isFieldActive(S.documents, "type") : true;

  const onFile = async (i, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;

    setUploadingIndex(i);
    try {
      const fileBase64 = await readFileAsBase64(f);
      setPatient((s) => ({
        ...s,
        documents: s.documents.map((doc, idx) => (
          idx === i ? { ...doc, fileName: f.name, fileBase64 } : doc
        )),
      }));
    } catch {
      toast.error("Could not read the selected file.");
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <Box>
      {patient.documents.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 2, border: 1, borderStyle: "dashed", borderRadius: 1, mb: 2 }}>
          No documents captured. Add Passport, Voter ID, National ID, Iqama, Aadhaar, etc.
        </Typography>
      )}
      {errors.documents__min && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {errors.documents__min}
        </Typography>
      )}
      {patient.documents.map((d, i) => (
        <Box key={i} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <ConfigurableField sectionId={S.documents} fieldName="type" xs={12} sm={6} md={md}>
              {({ required }) => (
                <FormField label="Document Type" required={required}>
                  <IpdTextField select fullWidth size="small" value={d.type} onChange={(e) => updateListItem("documents", i, "type", e.target.value)}>
                    {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>)}
                  </IpdTextField>
                </FormField>
              )}
            </ConfigurableField>
            <ConfigurableField sectionId={S.documents} fieldName="number" xs={12} sm={6} md={md}>
              {({ required }) => {
                const lengthRule = getDocumentNumberLengthRule(d.type);
                const numberError = errors[`documents.${i}.number`];
                return (
                  <FormField
                    label="Document Number"
                    required={required}
                    error={numberError}
                  >
                    <IpdTextField
                      fullWidth
                      size="small"
                      value={d.number}
                      onChange={(e) => updateListItem("documents", i, "number", e.target.value)}
                      error={Boolean(numberError)}
                      inputProps={lengthRule ? { maxLength: lengthRule.max } : undefined}
                    />
                  </FormField>
                );
              }}
            </ConfigurableField>
            <ConfigurableField sectionId={S.documents} fieldName="issuedBy" xs={12} sm={6} md={md}>
              {({ required }) => (
                <FormField label="Issued By" required={required}>
                  <IpdTextField fullWidth size="small" placeholder="Issuing authority" value={d.issuedBy} onChange={(e) => updateListItem("documents", i, "issuedBy", e.target.value)} />
                </FormField>
              )}
            </ConfigurableField>
            <ConfigurableField sectionId={S.documents} fieldName="expiry" xs={12} sm={6} md={md}>
              {({ required }) => (
                <FormField label="Expiry" required={required}>
                  <IpdDateField
                    value={d.expiry}
                    onChange={(value) => updateListItem("documents", i, "expiry", value)}
                  />
                </FormField>
              )}
            </ConfigurableField>
            {(!config || isFieldActive(S.documents, "fileName")) && (
              <Grid item xs={12} sm={6} md={md}>
                <FormField label="Attachment" required={config ? isFieldMandatory(S.documents, "fileName") : false}>
                  <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploadingIndex === i}>
                    {uploadingIndex === i ? "Reading…" : (d.fileName ? "Replace" : "Choose")}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => onFile(i, e)} />
                  </Button>
                </FormField>
              </Grid>
            )}
            {showRemove && canRemoveListEntry(S.documents, patient.documents.length) && (
              <Grid item xs={12} sm={6} md={1}>
                <IconButton aria-label="Remove document" onClick={() => removeListItem("documents", i)}><DeleteIcon /></IconButton>
              </Grid>
            )}
            {d.fileName && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Attached: {d.fileName}</Typography>
              </Grid>
            )}
            {d.type === "PASSPORT" && (
              <Grid item xs={12}>
                <Grid container spacing={2} sx={{ bgcolor: "action.hover", p: 1, borderRadius: 1 }}>
                  <Grid item xs={12} md={6}>
                    <FormField label="Visa Number">
                      <IpdTextField fullWidth size="small" value={d.visaNumber || ""} onChange={(e) => updateListItem("documents", i, "visaNumber", e.target.value)} />
                    </FormField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormField label="Visa Expiry">
                      <IpdDateField
                        value={d.visaExpiry || ""}
                        onChange={(value) => updateListItem("documents", i, "visaExpiry", value)}
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
      {canAddListEntry(S.documents, patient.documents.length) && (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addListItem("documents", { ...DEFAULT_DOC })}>
          Add Document
        </Button>
      )}
    </Box>
  );
}

export default memo(Documents);
